import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Archive, ArchiveRestore, ArrowRight, MoreVertical, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useArchiveProject, useDeleteProject, useUnarchiveProject } from '../../../hooks/useProjects';
import { useProposalProject } from '../../../hooks/useProposalProject';
import { ConvertToPreAwardDialog } from '../ConvertToPreAwardDialog';
/**
 * Owner-only Archive / Unarchive / Delete / Convert menu for the V2 proposal
 * shell. DashboardLayout renders it in the project top strip beside the
 * project selector, so it is reachable from every V2 sub-page (home, sources,
 * compliance, etc.) without covering any page's heading.
 */ export function ProjectActionsMenu() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { data } = useProposalProject(id ?? '');
    const archiveProject = useArchiveProject();
    const unarchiveProject = useUnarchiveProject();
    const deleteProject = useDeleteProject();
    const [showArchiveDialog, setShowArchiveDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showConvertDialog, setShowConvertDialog] = useState(false);
    if (!id || !data) return null;
    const isOwner = data.role === 'owner';
    if (!isOwner) return null;
    const isArchived = data.project.status === 'archived';
    const projectName = data.project.name;
    const anyPending = archiveProject.isPending || unarchiveProject.isPending || deleteProject.isPending;
    const handleArchive = async ()=>{
        try {
            await archiveProject.mutateAsync(id);
            toast.success('Proposal archived');
            setShowArchiveDialog(false);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to archive proposal');
        }
    };
    const handleUnarchive = async ()=>{
        try {
            await unarchiveProject.mutateAsync(id);
            toast.success('Proposal restored');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to restore proposal');
        }
    };
    const handleDelete = async ()=>{
        try {
            await deleteProject.mutateAsync(id);
            toast.success('Proposal deleted permanently');
            setShowDeleteDialog(false);
            navigate('/projects');
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to delete proposal');
        }
    };
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex-shrink-0",
                children: /*#__PURE__*/ _jsxs(DropdownMenu, {
                    children: [
                        /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                            asChild: true,
                            children: /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                disabled: anyPending,
                                "aria-label": "Project actions",
                                className: "inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50",
                                children: /*#__PURE__*/ _jsx(MoreVertical, {
                                    className: "h-4 w-4",
                                    "aria-hidden": "true"
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                            align: "start",
                            className: "w-52",
                            children: [
                                !isArchived && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                            onClick: ()=>setShowConvertDialog(true),
                                            className: "cursor-pointer",
                                            "data-testid": Testids.preAward.convertButton,
                                            children: [
                                                /*#__PURE__*/ _jsx(ArrowRight, {
                                                    className: "mr-2 h-4 w-4"
                                                }),
                                                "Promote to Pre-Award"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(DropdownMenuSeparator, {})
                                    ]
                                }),
                                isArchived ? /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                    onClick: handleUnarchive,
                                    disabled: unarchiveProject.isPending,
                                    className: "cursor-pointer",
                                    children: [
                                        /*#__PURE__*/ _jsx(ArchiveRestore, {
                                            className: "mr-2 h-4 w-4"
                                        }),
                                        "Restore Proposal"
                                    ]
                                }) : /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                    onClick: ()=>setShowArchiveDialog(true),
                                    disabled: archiveProject.isPending,
                                    className: "cursor-pointer text-yellow-700 focus:bg-yellow-50 focus:text-yellow-800",
                                    children: [
                                        /*#__PURE__*/ _jsx(Archive, {
                                            className: "mr-2 h-4 w-4"
                                        }),
                                        "Archive Proposal"
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(DropdownMenuSeparator, {}),
                                /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                    onClick: ()=>setShowDeleteDialog(true),
                                    disabled: deleteProject.isPending,
                                    className: "cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-700",
                                    children: [
                                        /*#__PURE__*/ _jsx(Trash2, {
                                            className: "mr-2 h-4 w-4"
                                        }),
                                        "Delete Permanently"
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: showArchiveDialog,
                onOpenChange: setShowArchiveDialog,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Archive proposal?"
                                }),
                                /*#__PURE__*/ _jsxs(AlertDialogDescription, {
                                    children: [
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: projectName
                                        }),
                                        " will be hidden from the active projects list. You can restore it any time from the archived projects view."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: handleArchive,
                                    className: "bg-yellow-600 hover:bg-yellow-700",
                                    children: "Archive"
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(ConvertToPreAwardDialog, {
                open: showConvertDialog,
                onOpenChange: setShowConvertDialog,
                project: data.project
            }),
            /*#__PURE__*/ _jsx(AlertDialog, {
                open: showDeleteDialog,
                onOpenChange: setShowDeleteDialog,
                children: /*#__PURE__*/ _jsxs(AlertDialogContent, {
                    children: [
                        /*#__PURE__*/ _jsxs(AlertDialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogTitle, {
                                    children: "Delete proposal permanently?"
                                }),
                                /*#__PURE__*/ _jsxs(AlertDialogDescription, {
                                    children: [
                                        "This permanently deletes ",
                                        /*#__PURE__*/ _jsx("strong", {
                                            children: projectName
                                        }),
                                        " and all associated data — files, chats, requirements, CLINs, LCATs, pricing, outline, and team members. This cannot be undone."
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(AlertDialogFooter, {
                            children: [
                                /*#__PURE__*/ _jsx(AlertDialogCancel, {
                                    children: "Cancel"
                                }),
                                /*#__PURE__*/ _jsx(AlertDialogAction, {
                                    onClick: handleDelete,
                                    className: "bg-red-600 hover:bg-red-700",
                                    children: "Delete Permanently"
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
